import Vue from 'vue';

/**
 * Shared look of the Statistics page widgets: the palette (read from the app's CSS variables, so it follows the
 * light / dark theme), ApexCharts base options and number formatting.
 *
 * `themeState.tick` changes whenever <html data-theme> changes (the page observes it): computed chart options that read
 * it are rebuilt with the new colours.
 */
export const themeState = Vue.observable({ tick: 0 });

let observer = null;
let observers = 0;

/** Starts following theme changes (call from the page's mounted / beforeDestroy). */
export function watchTheme() {
  observers += 1;
  if (!observer) {
    observer = new MutationObserver(() => { themeState.tick += 1; });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  }
  return () => {
    observers -= 1;
    if (observers === 0 && observer) { observer.disconnect(); observer = null; }
  };
}

export const isDark = () => document.documentElement.getAttribute('data-theme') === 'dark';

export function cssVar(name, fallback) {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/** Colours of the page. Re-read on every call: cheap, and always matches the current theme. */
export function palette() {
  const dark = isDark();
  return {
    dark,
    green: cssVar('--accent-green', '#4f8c4a'),
    greenLight: cssVar('--accent-green-light', '#9ed77f'),
    greenDark: cssVar('--accent-green-dark', '#3d7a3f'),
    teal: cssVar('--co2-accent', '#159895'),
    amber: dark ? '#f4c25b' : '#e0a52e',
    coral: dark ? '#f2875f' : '#e06a44',
    violet: dark ? '#a594f9' : '#7c66e8',
    sky: dark ? '#5fb4f0' : '#3b8fd4',
    rose: dark ? '#f27aa3' : '#d9578a',
    text: cssVar('--text-main', '#1f2a22'),
    heading: cssVar('--text-heading', '#0f1a12'),
    muted: cssVar('--text-muted', '#647368'),
    border: cssVar('--border-color', '#e3e8e4'),
    card: cssVar('--card-bg', '#ffffff'),
  };
}

/** The series colours, in order. */
export const seriesColors = (p = palette()) => [p.green, p.teal, p.amber, p.violet, p.sky, p.coral, p.rose, p.greenLight];

function isObject(v) { return v && typeof v === 'object' && !Array.isArray(v); }
export function merge(target, ...sources) {
  for (const src of sources) {
    if (!isObject(src)) continue;
    for (const [k, v] of Object.entries(src)) {
      if (isObject(v)) target[k] = merge(isObject(target[k]) ? target[k] : {}, v);
      else target[k] = v;
    }
  }
  return target;
}

/** ApexCharts options every chart starts from. */
export function apexBase(extra = {}) {
  void themeState.tick; // rebuild on theme change
  const p = palette();
  return merge({
    chart: {
      background: 'transparent',
      fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      foreColor: p.muted,
      toolbar: { show: false },
      zoom: { enabled: false },
      parentHeightOffset: 0,
      redrawOnParentResize: true,
      animations: { enabled: true, easing: 'easeinout', speed: 900, animateGradually: { enabled: true, delay: 90 }, dynamicAnimation: { enabled: true, speed: 450 } },
      dropShadow: { enabled: false },
    },
    theme: { mode: p.dark ? 'dark' : 'light' },
    colors: seriesColors(p),
    grid: { borderColor: p.border, strokeDashArray: 4, padding: { left: 6, right: 6, top: -8, bottom: -4 } },
    dataLabels: { enabled: false },
    legend: { labels: { colors: p.muted }, fontWeight: 600, markers: { radius: 12 }, itemMargin: { horizontal: 8 } },
    tooltip: { theme: p.dark ? 'dark' : 'light', style: { fontSize: '12px' } },
    stroke: { curve: 'smooth', width: 2.5 },
    xaxis: { axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: '11px' } } },
    yaxis: { labels: { style: { fontSize: '11px' } } },
    states: { hover: { filter: { type: 'lighten', value: 0.06 } }, active: { filter: { type: 'none' } } },
  }, extra);
}

const nf = (d) => new Intl.NumberFormat('en-GB', { minimumFractionDigits: d, maximumFractionDigits: d });
export const fmt = (n, d = 0) => nf(d).format(Number(n) || 0);

/** 1234567 -> "1.23M", 12345 -> "12.3k" */
export function compact(n) {
  const v = Number(n) || 0;
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toFixed(a >= 1e10 ? 1 : 2)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(a >= 1e7 ? 1 : 2)}M`;
  if (a >= 1e4) return `${(v / 1e3).toFixed(a >= 1e5 ? 0 : 1)}k`;
  return fmt(v, a < 10 && v % 1 ? 1 : 0);
}

/** "2026-03" -> "Mar 26" */
export function monthLabel(m) {
  const [y, mo] = String(m).split('-').map(Number);
  if (!y || !mo) return m;
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });
}

export function timeAgo(d) {
  const t = new Date(d).getTime();
  if (!t) return '';
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  const m = Math.round(s / 60); if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60); if (h < 48) return `${h} h ago`;
  const days = Math.round(h / 24); if (days < 60) return `${days} days ago`;
  const mo = Math.round(days / 30); if (mo < 24) return `${mo} months ago`;
  return `${Math.round(mo / 12)} years ago`;
}

export const imageUrl = (path) => (path ? (/^https?:/.test(path) ? path : (process.env.VUE_APP_IMAGE_URL || '') + path) : null);
